/** Ordered — index 0 is the first step; `nextStepKey` walks this order. */
export const CAP_JOURNEY_STEP_ORDER = [
    'ccds',
    'plos',
    'matrix',
    'audit',
    'proposal',
];
/**
 * Derive the 5-step CAP journey from the compliance report + snapshot/proposal
 * state. Each step's `done` is an OBJECTIVE data signal, not a manual check:
 *   1. ccds     — every curriculum course has a CCD (no `coursesWithoutCcd`) and
 *                 the program actually maps courses (guards the empty-malla case,
 *                 where an empty `coursesWithoutCcd` would otherwise read "done").
 *   2. plos     — the program has ≥1 PLO defined (audit surfaced coverage rows).
 *   3. matrix   — every PLO carries an 'E' (all coverage rows `evaluated`).
 *   4. audit    — a compliance snapshot has been saved (frozen evidence).
 *   5. proposal — the proposal has been submitted.
 */
export function computeCapJourneyProgress(input) {
    const { report, hasSavedSnapshot, savedStatus, proposalStatus } = input;
    const ploTotal = report?.ploCoverage.length ?? 0;
    const ploEvaluated = report?.ploCoverage.filter((p) => p.evaluated).length ?? 0;
    const coursesWithoutCcd = report?.coursesWithoutCcd.length ?? 0;
    const gapTcu = report ? Math.max(0, report.totalMinTcu - report.totalActualTcu) : 0;
    // A program "maps courses" when its CCDs contribute TCU to a required area, or
    // at least land in a (non-required) valid area — distinguishes a real curriculum
    // from an empty program, where `coursesWithoutCcd` is trivially empty.
    const mapsCourses = !!report && (report.totalActualTcu > 0 || report.warnings.coursesOutsideCapAreas.length > 0);
    const ccdsDone = !!report && coursesWithoutCcd === 0 && mapsCourses;
    const plosDone = ploTotal > 0;
    const matrixDone = ploTotal > 0 && ploEvaluated === ploTotal;
    const auditDone = hasSavedSnapshot;
    const proposalDone = proposalStatus === 'submitted';
    // `exactOptionalPropertyTypes` — omit `status` rather than set it to undefined.
    const auditDetail = { count: gapTcu };
    if (hasSavedSnapshot && savedStatus)
        auditDetail.status = savedStatus;
    const steps = [
        { key: 'ccds', done: ccdsDone, detail: { count: coursesWithoutCcd } },
        { key: 'plos', done: plosDone, detail: { count: ploTotal } },
        { key: 'matrix', done: matrixDone, detail: { count: ploEvaluated, total: ploTotal } },
        { key: 'audit', done: auditDone, detail: auditDetail },
        { key: 'proposal', done: proposalDone, detail: { status: proposalStatus ?? 'none' } },
    ];
    const doneCount = steps.filter((s) => s.done).length;
    const totalCount = steps.length;
    const pct = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);
    const nextStepKey = CAP_JOURNEY_STEP_ORDER.find((k) => !steps.find((s) => s.key === k).done) ?? null;
    return { steps, doneCount, totalCount, pct, nextStepKey };
}
//# sourceMappingURL=cap-journey.service.js.map